import { toMoney } from "shared/utils";
import { Warning } from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified/common";
import {
  PaystubDocumentPaystub,
  PaystubInscribeAdditionalData
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified/paystub";
import { AuthenticityScoreAlert } from "views/applications-id-report/slices/CombinedIncomeReportSlice/components/AuthenticityScoreAlert";
import { resolveAuthenticityTone } from "views/applications-id-report/slices/CombinedIncomeReportSlice/utils/authenticityTone";
import {
  resolveAuthenticityScore,
  resolveFraudRisk,
  resolveRiskSummary
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/utils/fraudRisk";
import {
  NAME_MISMATCH_LABEL,
  resolveDocumentProblems,
  toFraudRiskLines
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/utils/documentProblems";
import { DocumentIssueAlert } from "views/applications-id-report/slices/CombinedIncomeReportSlice/components/DocumentIssueAlert";
import { SimpleTable } from "../components/SimpleTable";
import { EmployeeInformationSlice } from "./EmployeeInformationSlice";
import { EmployerInformationSlice } from "./EmployerInformationSlice";
import { PaystubDetailsSlice } from "./PaystubDetailsSlice";
import { NameMismatchAlert } from "../../NameMismatchAlert";
import { Show } from "shared/components";
import {
  DocumentVerificationProvider,
  IncomeStatus,
  VerifyMethodType
} from "shared/types";
import { ReasonCode } from "types/landlord/reports";
import { getPaystubReasonCodes } from "../utils";
import { rejectionReasonsToIssues } from "../../../utils";

type InscribeRowData = {
  sourceStatus?: IncomeStatus;
  additional_data?: PaystubInscribeAdditionalData;
  trust_score?: number;
  rejection_reason?: string[] | null;
};

type ExpandedRowData = (PaystubDocumentPaystub | Warning) & InscribeRowData;

export const ExpandedTableRow = ({
  rowData,
  provider
}: {
  rowData: ExpandedRowData;
  provider: DocumentVerificationProvider;
}) => {
  const isInscribe = provider === DocumentVerificationProvider.Inscribe;
  const isWarningEntity = "doc_info" in rowData;
  const nameMismatch = isWarningEntity ? null : rowData.name_mismatch;

  let docScore: number;
  let reasonCodes: ReasonCode[];

  if (isInscribe) {
    docScore = rowData?.trust_score ?? 0;
    reasonCodes = getPaystubReasonCodes(rowData?.additional_data);
  } else {
    const formAnalysis = isWarningEntity
      ? rowData?.form_analysis
      : rowData?.doc_analysis?.form_analysis;
    docScore = formAnalysis?.[0]?.form_authenticity?.score ?? 0;
    reasonCodes = formAnalysis?.[0]?.form_authenticity?.reason_codes ?? [];
  }

  const riskLevel = resolveFraudRisk(rowData);
  const alertScore = resolveAuthenticityScore(rowData) ?? docScore;
  const problems = resolveDocumentProblems(rowData);
  const cardProblems = problems.filter(
    (problem) => problem.label !== NAME_MISMATCH_LABEL
  );
  const failedDocReasonCodes = rejectionReasonsToIssues(
    rowData.rejection_reason,
    VerifyMethodType.Paystub
  );

  return (
    <>
      <div className="space-y-3 mb-4">
        {nameMismatch ? (
          <NameMismatchAlert nameMismatch={nameMismatch} />
        ) : null}

        <Show if={problems.length > 1}>
          {cardProblems.map((problem) => (
            <DocumentIssueAlert
              key={problem.label}
              label={problem.label}
              description={problem.description}
            />
          ))}
        </Show>

        <AuthenticityScoreAlert
          tone={resolveAuthenticityTone(riskLevel, alertScore)}
          score={alertScore}
          issues={Array.from(
            new Set([
              ...toFraudRiskLines(reasonCodes, resolveRiskSummary(rowData)),
              ...toFraudRiskLines(
                failedDocReasonCodes,
                resolveRiskSummary(rowData)
              )
            ])
          )}
        />
      </div>

      {isWarningEntity ? null : (
        <>
          <PaystubDetailsSlice paystub={rowData} />
          <EmployerInformationSlice paystub={rowData} />
          <EmployeeInformationSlice paystub={rowData} />

          {/* Pay distribution table (currently Ocrolus only - Inscribe does not yet include this data) */}
          <Show if={Boolean(rowData?.pay_distribution?.length)}>
            <SimpleTable
              data={rowData?.pay_distribution ?? []}
              columns={[
                {
                  label: "Bank Name",
                  render: (d) => String(d.bank_name) || "-"
                },
                {
                  label: "Current pay",
                  render: (d) => toMoney((d.current_pay?.cents || 0) / 100)
                },
                {
                  label: "Account",
                  render: (d) => String(d.account_number) || "-"
                },
                {
                  label: "Type",
                  render: (d) => d.bank_account_type || "-"
                },
                {
                  label: "Description",
                  render: (d) => d.description || "-"
                }
              ]}
            />
          </Show>

          <div className="h-px w-full bg-alpha-100" />

          {/* Earnings table | Earning | Current pay | YTD pay | Cur. hrs | Description */}
          <Show if={Boolean(rowData?.earnings?.totals?.length)}>
            <SimpleTable
              data={rowData?.earnings?.totals ?? []}
              columns={[
                {
                  label: "Earning",
                  render: (e) => e.description || "-"
                },
                {
                  label: "Current pay",
                  render: (e) =>
                    !e.current_pay ? "-" : toMoney(e.current_pay.cents / 100)
                },
                {
                  label: "YTD pay",
                  render: (e) =>
                    !e.ytd_pay ? "-" : toMoney(e.ytd_pay.cents / 100)
                },
                {
                  label: "Current hours",
                  render: (e) => e.current_hours || "-"
                },
                {
                  label: "Description",
                  render: (e) => e.canonical_description || "-"
                }
              ]}
            />
          </Show>

          {/* Deduction table | Deduction | Current pay */}
          <Show if={Boolean(rowData?.deductions?.totals?.length)}>
            <SimpleTable
              data={rowData?.deductions?.totals ?? []}
              columns={[
                {
                  label: "Deduction",
                  render: (d) => d.description?.replaceAll("_", " ") || "-",
                  className: "w-1/5"
                },
                {
                  label: "Current pay",
                  render: (d) =>
                    !d.current_pay ? "-" : toMoney(d.current_pay.cents / 100)
                }
              ]}
            />
          </Show>
        </>
      )}
    </>
  );
};
